import { useEffect, useMemo, useState } from 'react';
import { CORPUS, corpusEntry } from '../../data/chorales/corpusIndex';
import { ColumnKind, FormColumn, FormRoom, KIND_WORDS, LETTER_KIND, describeKeys, formRoom } from '../../data/forms';
import { keyName, parseKey } from '../../music/key';
import type { PlayNote } from '../../audio/player';
import { usePlayer, usePrefs, useWidth } from '../hooks';
import { ReturnsMini } from './Returns';
import { PlayIcon } from '../components/Icons';
import { PlayButton } from '../components/PlayButton';
import { Crumbs } from '../components/Shell';
import { CompareTabs } from './Compare';

/* ---------- Shared ---------- */

const KINDS: ColumnKind[] = ['same', 'home', 'moved', 'varied', 'cut', 'new'];

/** Fill for a column kind: neutral for "as it was", validated series colours for the others. */
const FILL: Record<ColumnKind, string> = {
  same: 'var(--cell-same)',
  home: 'var(--series-1)',
  moved: 'var(--series-3)',
  varied: 'var(--series-4)',
  cut: 'none',
  new: 'none',
};

function Swatch({ kind }: { kind: ColumnKind }) {
  const hollow = kind === 'cut' || kind === 'new';
  return (
    <svg width="14" height="14" aria-hidden="true">
      <rect x="1" y="1" width="12" height="12" rx="3" fill={hollow ? 'none' : FILL[kind]} stroke={hollow ? 'var(--ink-2)' : 'none'} strokeWidth={1.2} className={hollow ? 'swatch-missing' : undefined} />
    </svg>
  );
}

function Legend({ counts, depth }: { counts: Record<ColumnKind, number>; depth: number }) {
  return (
    <div className="form-legend" role="list" aria-label="Legend">
      {KINDS.filter((k) => counts[k] > 0).map((k) => (
        <span key={k} role="listitem">
          <Swatch kind={k} /> {depth >= 2 ? KIND_WORDS[k].name : KIND_WORDS[k].word} <span className="muted">{counts[k]}</span>
        </span>
      ))}
    </div>
  );
}

/** Two rows of cells: the exposition above, the recapitulation below, one column per aligned bar. */
export function Strip({ kinds, width, height = 34, selected, onPick }: { kinds: ColumnKind[]; width: number; height?: number; selected?: number; onPick?: (i: number) => void }) {
  const w = width / kinds.length;
  const row = (height - 4) / 2;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: 'block' }} aria-hidden={!onPick}>
      {kinds.map((k, i) => {
        const hollow = k === 'cut' || k === 'new';
        const cell = (y: number, present: boolean) =>
          present ? (
            <rect x={i * w + 0.5} y={y} width={Math.max(0.5, w - 1)} height={row} rx={Math.min(3, w / 3)} fill={hollow ? 'none' : FILL[k]} stroke={hollow ? 'var(--ink-2)' : 'none'} strokeWidth={1} strokeDasharray={hollow ? '3 2' : undefined} />
          ) : (
            <line x1={i * w + 1} x2={(i + 1) * w - 1} y1={y + row / 2} y2={y + row / 2} className="s-grid" />
          );
        return (
          <g key={i} onClick={onPick ? () => onPick(i) : undefined} style={onPick ? { cursor: 'pointer' } : undefined}>
            {onPick && <rect x={i * w} y={0} width={w} height={height} fill="transparent" />}
            {cell(0, k !== 'new')}
            {cell(row + 4, k !== 'cut')}
            {i === selected && <rect x={i * w} y={0} width={w} height={height} fill="none" stroke="var(--ink)" strokeWidth={1.5} rx={2} />}
          </g>
        );
      })}
    </svg>
  );
}

function sentence(counts: Record<ColumnKind, number>): string {
  const expoBars = counts.same + counts.home + counts.moved + counts.varied + counts.cut;
  const parts = [
    counts.same && `${counts.same} return as they were`,
    counts.home && `${counts.home} come back brought home`,
    counts.moved && `${counts.moved} return in another key`,
    counts.varied && `${counts.varied} are rewritten`,
    counts.cut && `${counts.cut} are left out`,
  ].filter(Boolean);
  return `Of ${expoBars} bars in the exposition, ${parts.join(', ')}.${counts.new ? ` ${counts.new} bars are new in the recapitulation.` : ''}`;
}

/* ---------- Index ---------- */

export function FormsIndex() {
  const { prefs } = usePrefs();
  const forms = CORPUS.forms;
  const total = forms.reduce(
    (a, f) => {
      KINDS.forEach((k) => (a[k] += f.counts[k]));
      return a;
    },
    { same: 0, home: 0, moved: 0, varied: 0, cut: 0, new: 0 } as Record<ColumnKind, number>,
  );
  const sonataReturns = CORPUS.returns.filter((r) => corpusEntry(r.id)?.kind === 'sonata');
  if (!forms.length && !sonataReturns.length)
    return (
      <div className="page-head">
        <span className="label">Comparisons</span>
        <h1>Not in this edition</h1>
        <p>
          The sonata comparisons are built from the Mozart piano sonatas, whose scores are licensed for non-commercial use only, and this edition of the atlas leaves them
          out. Two kinds of comparison remain: <a href="#variations">themes and their variations</a> by Mozart and Beethoven, and Bach compared with himself in{' '}
          <a href="#tunes">one tune, many harmonies</a>.
        </p>
      </div>
    );
  return (
    <div>
      <header className="page-head">
        <span className="label">Compare</span>
        <h1>When the music returns</h1>
        <p>
          A sonata movement plays its themes twice: first setting out and moving away ({prefs.depth >= 2 ? 'the exposition' : 'the first time'}), then coming back (
          {prefs.depth >= 2 ? 'the recapitulation' : 'the return'}). Open a movement to hear, bar by bar, what Mozart changed so the music can stay at home.
        </p>
        <p className="hint">Each strip lines up the two journeys bar by bar, the first time above and the return below; the colours are explained just below.</p>
      </header>
      <CompareTabs current="returns" />
      <Legend counts={total} depth={prefs.depth} />
      <div className="path-grid" style={{ marginTop: 14 }}>
        {forms.map((f) => {
          const e = corpusEntry(f.id);
          return (
            <a key={f.id} className="path-card" href={`#form.${f.id}`}>
              <h3 style={{ fontSize: '1.05rem' }}>{e?.title ?? f.id}</h3>
              <span className="muted" style={{ fontSize: '0.8rem' }}>
                {e?.catalogue} · {e?.subtitle} · {f.counts.home} bars brought home · {f.counts.varied} rewritten
              </span>
              <span className="glyph" style={{ display: 'block' }}>
                <Strip kinds={[...f.strip].map((ch) => LETTER_KIND[ch])} width={200} height={26} />
              </span>
            </a>
          );
        })}
      </div>
      {sonataReturns.length > 0 && (
        <>
          <header className="page-head" style={{ marginTop: 36 }}>
            <span className="label">The same music, again and again</span>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', margin: '4px 0 6px' }}>Refrains and variations</h2>
            <p>
              A rondo keeps coming back to its refrain; a set of variations keeps coming back to its theme. Each picture lines up every return under the first statement,
              one row per return: grey bars come back note for note, green bars come back in another key, blue bars keep the harmony under a new surface, yellow bars are
              rewritten, hollow bars are left out. For whole sets of variations by Mozart and Beethoven, see <a href="#variations">the same harmony, new clothes</a>.
            </p>
          </header>
          <div className="path-grid">
            {sonataReturns.map((r) => {
              const e = corpusEntry(r.id);
              return (
                <a key={r.id} className="path-card" href={`#returns.${r.id}`}>
                  <h3 style={{ fontSize: '1.05rem' }}>{e?.title ?? r.id}</h3>
                  <span className="muted" style={{ fontSize: '0.8rem' }}>
                    {e?.catalogue} · {e?.subtitle} · {r.kind === 'variations' ? `theme and ${r.rows.length} variations` : `refrain and ${r.rows.length} ${r.rows.length === 1 ? 'return' : 'returns'}`}
                  </span>
                  <span className="glyph" style={{ display: 'block' }}>
                    <ReturnsMini rows={r.rows} />
                  </span>
                </a>
              );
            })}
          </div>
        </>
      )}
      <p className="foot">
        Bars are compared by their top and bass notes (and, for returns, their harmony) under the best transposition, and lined up by dynamic programming (see Method).
        Mozart scores: DCML, CC BY-NC-SA 4.0.
      </p>
    </div>
  );
}

/* ---------- Room ---------- */

interface Segment {
  label: string;
  col: number;
  offset: number;
  length: number;
}

export function FormRoomView({ id }: { id: string }) {
  const room = useMemo(() => formRoom(id), [id]);
  if (!room) {
    return (
      <div className="page-head">
        <h1>No such comparison</h1>
        <p>
          <a href="#forms">See all movements</a>
        </p>
      </div>
    );
  }
  return <Room key={room.id} room={room} />;
}

function Room({ room }: { room: FormRoom }) {
  const { prefs } = usePrefs();
  const { depth } = prefs;
  const { player, position, playing } = usePlayer();
  const firstTurn = room.runs[0] ? room.runs[0].from : 0;
  const [col, setCol] = useState(firstTurn);
  const [samePitch, setSamePitch] = useState(false);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [ref, width] = useWidth<HTMLDivElement>();
  const tempoScale = room.piece.tempo / 66;
  useEffect(() => player.setTempo(prefs.tempo * tempoScale), [player, prefs.tempo, tempoScale]);

  const measures = room.piece.measures;
  const barSpan = (bar: number, before = 1) => {
    const i = measures.findIndex((m) => m.bar === bar);
    const a = measures[Math.max(0, i - before)];
    const b = measures[i];
    return { start: a.start, end: b.start + b.length };
  };
  const excerpt = (start: number, end: number, offset: number, shift: number): PlayNote[] =>
    room.piece.notes
      .filter((n) => n.onset < end - 1e-6 && n.onset + n.dur > start + 1e-6)
      .map((n) => {
        const a = Math.max(n.onset, start);
        const b = Math.min(n.onset + n.dur, end);
        return { onset: offset + a - start, dur: b - a, midi: n.midi + shift, line: n.line, velocity: n.line === 0 ? 0.62 : 0.72 };
      });
  const signed = (s: number) => (s > 6 ? s - 12 : s);
  const play = (items: Array<{ label: string; col: number; start: number; end: number; shift: number }>) => {
    let t = 0;
    const notes: PlayNote[] = [];
    const segs: Segment[] = [];
    for (const it of items) {
      notes.push(...excerpt(it.start, it.end, t, it.shift));
      segs.push({ label: it.label, col: it.col, offset: t, length: it.end - it.start });
      t += it.end - it.start + 1.5;
    }
    setSegments(segs);
    player.setNotes(notes, Math.max(0, t - 1.5));
    void player.play(0);
  };
  const playColumn = (i: number) => {
    const c = room.columns[i];
    const items: Array<{ label: string; col: number; start: number; end: number; shift: number }> = [];
    if (c.e !== null) items.push({ label: `Exposition, bar ${c.e}`, col: i, ...barSpan(c.e), shift: samePitch && c.r !== null ? signed(c.shift) : 0 });
    if (c.r !== null) items.push({ label: `Recapitulation, bar ${c.r}`, col: i, ...barSpan(c.r), shift: 0 });
    play(items);
  };
  const nearest = (from: number, dir: 1 | -1, side: 'e' | 'r') => {
    for (let i = from; i >= 0 && i < room.columns.length; i += dir) if (room.columns[i][side] !== null) return room.columns[i][side] as number;
    return null;
  };
  const playRun = (ri: number) => {
    const run = room.runs[ri];
    const span = (side: 'e' | 'r') => {
      const a = nearest(Math.max(0, run.from - 1), -1, side) ?? nearest(run.from, 1, side);
      const b = nearest(Math.min(room.columns.length - 1, run.to + 1), 1, side) ?? nearest(run.to, -1, side);
      if (a === null || b === null) return null;
      const s = barSpan(a, 0);
      const e = barSpan(b, 0);
      return { start: s.start, end: e.end };
    };
    const es = span('e');
    const rs = span('r');
    const items: Array<{ label: string; col: number; start: number; end: number; shift: number }> = [];
    if (es) items.push({ label: 'Exposition', col: run.from, ...es, shift: 0 });
    if (rs) items.push({ label: 'Recapitulation', col: run.from, ...rs, shift: 0 });
    setCol(run.from);
    play(items);
  };
  const pick = (i: number) => {
    setCol(i);
    playColumn(i);
  };

  const current = playing ? [...segments].reverse().find((s) => position >= s.offset - 1e-6) : undefined;
  const c: FormColumn = room.columns[col];
  const kinds = room.columns.map((x) => x.kind);
  const W = width || 800;
  const cw = W / room.columns.length;
  const sectionMarks = (side: 'e' | 'r') => {
    const secs = side === 'e' ? room.expo.sections : room.recap.sections;
    return secs
      .map((s) => {
        const idx = room.columns.findIndex((x) => x[side] !== null && x[side]! >= s.from);
        return idx >= 0 ? { idx, label: s.label } : null;
      })
      .filter((x): x is { idx: number; label: string } => x !== null);
  };
  const runWord = (k: string) => (k === 'new' ? 'only in the recapitulation' : k === 'cut' ? 'only in the exposition' : 'rewritten');
  const runBars = (r: { from: number; to: number }) => {
    const rb = room.columns.slice(r.from, r.to + 1).map((x) => x.r).filter((x): x is number => x !== null);
    const eb = room.columns.slice(r.from, r.to + 1).map((x) => x.e).filter((x): x is number => x !== null);
    const bars = rb.length ? rb : eb;
    const pre = rb.length ? 'bars' : 'exposition bars';
    return bars.length ? `${pre} ${bars[0]}${bars.length > 1 ? `–${bars[bars.length - 1]}` : ''}` : '';
  };

  return (
    <div className="form-room">
      <Crumbs items={[{ href: '#compare', label: 'Compare' }, { href: '#forms', label: 'When the music returns' }, { label: room.piece.catalogue }]} />
      <header className="page-head">
        <span className="label">{depth >= 2 ? 'Exposition and recapitulation' : 'The first time and the return'}</span>
        <h1>{room.piece.title}</h1>
        <p>
          {room.piece.catalogue}, {room.piece.subtitle}. {sentence(room.counts)} Pick a bar to hear it in the exposition and then in the recapitulation{samePitch ? ', the first moved to the pitch of the second' : ''}.
        </p>
      </header>

      <div className="transport" style={{ marginBottom: 12 }}>
        <PlayButton
          playing={playing}
          label={
            room.columns[col]?.e != null && room.columns[col]?.r != null
              ? `Hear bar ${room.columns[col].e}, then its return (bar ${room.columns[col].r})`
              : `Hear bar ${room.columns[col]?.e ?? room.columns[col]?.r}`
          }
          onPause={() => player.pause()}
          onPlay={() => playColumn(col)}
        />
        <span className="where">{current ? current.label : ''}</span>
        <span className="grow" />
        <label className="field">
          <input type="checkbox" checked={samePitch} onChange={(e) => setSamePitch(e.target.checked)} /> Hear the exposition at the recapitulation’s pitch
        </label>
        <a className="btn quiet" href={`#${room.id}`}>
          Open the movement
        </a>
      </div>

      {room.runs.length > 0 && (
        <div className="chips hot-chips" role="group" aria-label="Where the recapitulation turns">
          <span className="label">Where it turns</span>
          {room.runs.slice(0, 8).map((r, i) => (
            <button key={i} className="chip" aria-pressed={col >= r.from && col <= r.to} onClick={() => playRun(i)}>
              {runBars(r)} <span className="muted">· {runWord(r.kind)}</span>
            </button>
          ))}
        </div>
      )}

      <div className="panel" style={{ padding: '14px 16px' }}>
        <Legend counts={room.counts} depth={depth} />
        <div className="form-grid grid-scroll-x" style={{ marginTop: 10, ['--cols' as string]: room.columns.length }}>
          <span />
          <div ref={ref}>
            {width > 0 && (
              <svg width={W} height={16} viewBox={`0 0 ${W} 16`} aria-hidden="true" style={{ display: 'block' }}>
                {sectionMarks('e').map((s, i) => (
                  <g key={i}>
                    <line x1={s.idx * cw} x2={s.idx * cw} y1={4} y2={16} className="s-ink" />
                    <text x={s.idx * cw + 3} y={11} className="t-small">
                      {s.label}
                    </text>
                  </g>
                ))}
              </svg>
            )}
          </div>
          <div className="form-row-labels">
            <span>{depth >= 2 ? 'Exposition' : 'First time'}</span>
            <span>{depth >= 2 ? 'Recapitulation' : 'The return'}</span>
          </div>
          <div role="group" aria-label="Bars, exposition above and recapitulation below">
            <Strip kinds={kinds} width={W} height={64} selected={col} onPick={pick} />
          </div>
          <span />
          <div>
            {width > 0 && (
              <svg width={W} height={16} viewBox={`0 0 ${W} 16`} aria-hidden="true" style={{ display: 'block' }}>
                {sectionMarks('r').map((s, i) => (
                  <g key={i}>
                    <line x1={s.idx * cw} x2={s.idx * cw} y1={0} y2={12} className="s-ink" />
                    <text x={s.idx * cw + 3} y={12} className="t-small">
                      {s.label}
                    </text>
                  </g>
                ))}
              </svg>
            )}
          </div>
        </div>
      </div>

      <section className="panel" style={{ marginTop: 14 }} aria-live="polite">
        <div className="panel-head">
          <span className="label">
            {c.e !== null ? `Exposition bar ${c.e}` : 'Not in the exposition'} · {c.r !== null ? `recapitulation bar ${c.r}` : 'not in the recapitulation'}
          </span>
          <span style={{ display: 'flex', gap: 6 }}>
            <button className="btn" onClick={() => setCol((i) => Math.max(0, i - 1))} aria-label="Previous bar">
              ←
            </button>
            <button className="btn" onClick={() => setCol((i) => Math.min(room.columns.length - 1, i + 1))} aria-label="Next bar">
              →
            </button>
          </span>
        </div>
        <h2 style={{ fontSize: '1.5rem', margin: '4px 0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Swatch kind={c.kind} /> {KIND_WORDS[c.kind].word}
        </h2>
        <p style={{ margin: 0 }}>
          {c.kind === 'home' &&
            (c.modeChange
              ? `The same music, moved from ${keyName(c.eKey!)} to ${keyName(c.rKey!)}: brought home, and turned to the ${c.rKey!.mode} mode on the way.`
              : `The same music, moved from ${keyName(c.eKey!)} to ${keyName(c.rKey!)}: the music that was away now stays at home.`)}
          {c.kind === 'same' && `Note for note as before${c.eKey ? `, in ${keyName(c.eKey)}` : ''}.`}
          {c.kind === 'moved' && `The same music in another key (${describeKeys(c)}).`}
          {c.kind === 'varied' && `Mozart changes this bar${c.eKey && c.rKey ? ` (${describeKeys(c)})` : ''}; only ${Math.round(c.sim * 100)}% of its top and bass notes match.`}
          {c.kind === 'cut' && 'This bar has no counterpart in the recapitulation.'}
          {c.kind === 'new' && 'This bar has no counterpart in the exposition: here the recapitulation takes its own way.'}
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => playColumn(col)}>
            <PlayIcon /> Hear {c.e !== null && c.r !== null ? 'both' : 'it'}
          </button>
        </div>
        <p className="muted" style={{ fontSize: '0.84rem', marginTop: 12 }}>
          Each excerpt starts one bar early, so you hear how the bar is reached. Home key: {keyName(parseKey(room.piece.homeKey))}. Bars are compared by their top and bass
          notes; the rules cannot tell a written-out ornament from a real change, so listen before trusting a label.
        </p>
      </section>
    </div>
  );
}
